import { AuditOperationType, AuditTrailEntryDto } from '@common/audit-trail/auditTrailTypes';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { authorInitials, authorLabel, avatarColors, rowIdLabel, shortTxId } from '@common/audit-trail/auditTrailFormat';
import { auditTrailRowIdPrefix } from './auditTrailLabels';

const OPERATION_DOT_CLASSES: Record<AuditOperationType, string> = {
    Created: 'bg-success',
    Updated: 'bg-info',
    Deleted: 'bg-destructive',
};

const OPERATION_PILL_CLASSES: Record<AuditOperationType, string> = {
    Created: 'bg-success-lighter text-fg-success',
    Updated: 'bg-[#F1F1FF] text-[#5B5BC7] new:bg-info-lighter new:text-info',
    Deleted: 'bg-[#FBEDED] text-error-darker new:bg-danger-tint',
};

export function operationLabel(operationType: AuditOperationType): string {
    return t(`audit-trail.filter-${operationType.toLowerCase()}`);
}

export function ActionDot({ operationType }: { operationType: AuditOperationType }) {
    return <span className={cn('size-2 flex-none rounded-full', OPERATION_DOT_CLASSES[operationType])} />;
}

export function RowIdButton({
    rowId,
    label,
    className,
    onClick,
}: {
    rowId: string | null;
    /** Resolved name of the row; when set it replaces the raw id, which moves to the tooltip. */
    label?: string | null;
    className?: string;
    onClick: (rowId: string) => void;
}) {
    if (!rowId) {
        return <span className={cn('text-grey-500 font-mono', className)}>-</span>;
    }
    const filterHint = t('audit-trail.filter-by-record');
    return (
        <button
            type="button"
            title={label ? `${rowIdLabel(rowId, auditTrailRowIdPrefix())} · ${filterHint}` : filterHint}
            className={cn(
                'text-grey-500 new:hover:text-primary-ink max-w-80 cursor-pointer truncate hover:text-[#4B7049] hover:underline',
                label ? 'font-medium' : 'font-mono',
                className
            )}
            onClick={() => onClick(rowId)}
        >
            {label ?? rowIdLabel(rowId, auditTrailRowIdPrefix())}
        </button>
    );
}

export function AuthorName({
    entry,
    className,
    onClick,
}: {
    entry: Pick<AuditTrailEntryDto, 'author' | 'changedBy' | 'authorId'>;
    className?: string;
    onClick: (authorId: number) => void;
}) {
    const { authorId } = entry;
    if (authorId === null) {
        return <span className={className}>{authorLabel(entry)}</span>;
    }
    return (
        <button
            type="button"
            title={t('audit-trail.filter-by-author')}
            className={cn('cursor-pointer hover:underline', className)}
            onClick={() => onClick(authorId)}
        >
            {authorLabel(entry)}
        </button>
    );
}

export function ActionPill({ operationType, className }: { operationType: AuditOperationType; className?: string }) {
    return (
        <span
            className={cn(
                'new:text-caption rounded-full px-2 py-px text-[10.5px] font-semibold tracking-[0.01em]',
                OPERATION_PILL_CLASSES[operationType],
                className
            )}
        >
            {operationLabel(operationType)}
        </span>
    );
}

export function EntityTypeBadge({ label }: { label: string }) {
    return (
        <span className="text-grey-600 new:bg-muted new:text-caption rounded-md bg-[#F4F4F4] px-2 py-0.5 text-[11px] font-semibold">
            {label}
        </span>
    );
}

export function AuthorAvatar({
    entry,
    size,
}: {
    entry: Pick<AuditTrailEntryDto, 'author' | 'changedBy' | 'authorId'>;
    size: 'sm' | 'lg';
}) {
    const colors = avatarColors(entry);
    return (
        <span
            className={cn(
                'flex flex-none items-center justify-center rounded-full font-semibold tracking-[0.02em]',
                size === 'sm' ? 'new:text-caption size-[26px] text-[10px]' : 'size-[38px] text-[13px]'
            )}
            style={{ background: colors.background, color: colors.text }}
        >
            {authorInitials(entry)}
        </span>
    );
}

export function TransactionButton({
    transactionId,
    onClick,
}: {
    transactionId: string;
    onClick: (transactionId: string) => void;
}) {
    return (
        <button
            type="button"
            title={t('audit-trail.filter-by-transaction')}
            className="bg-grey-100 text-grey-500 new:hover:bg-primary-wash new:hover:text-primary-ink new:text-caption cursor-pointer rounded-md px-2 py-[3px] font-mono text-[10.5px] hover:bg-[#EDF3ED] hover:text-[#4B7049]"
            onClick={() => onClick(transactionId)}
        >
            {shortTxId(transactionId)}
        </button>
    );
}
